import React, { useState, useEffect, useRef, useContext } from 'react'
import { Search as SearchIcon, ChevronRight } from 'lucide-react'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import { useDebounce } from '@/hooks/useDebounce'
import { useNavigate } from 'react-router-dom'
import { DashboardContext } from '@/context/DashboardContext'
import { cn } from '@/lib/utils'

interface Suggestion {
  id: string
  type: 'product' | 'project' | 'meeting'
  label: string
  path: string
  date?: number | string
}

// Inline SVGs matching sidebar icons
const ProductIcon = ({ size = 16, className = 'text-gray-500' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_gs_prod)">
      <path
        d="M10.5556 2.5L18.1111 6.75V15.25L10.5556 19.5L3 15.25V6.75L10.5556 2.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.5566 11L18.1122 6.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10.5566 11V19.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M10.5556 11L3 6.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.3329 4.625L6.77734 8.875"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_gs_prod">
        <rect width="20" height="20" fill="white" transform="translate(0 0.5)" />
      </clipPath>
    </defs>
  </svg>
)

const ProjectIcon = ({ size = 16, className = 'text-gray-500' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M2 3H17.9994V14.25C17.9994 14.4489 17.9228 14.6397 17.7864 14.7803C17.65 14.921 17.465 15 17.2721 15H13.6359C13.443 15 13.2581 14.921 13.1217 14.7803C12.9853 14.6397 12.9087 14.4489 12.9087 14.25V12H7.09071V17.25C7.09071 17.4489 7.01409 17.6397 6.8777 17.7803C6.74132 17.921 6.55634 18 6.36346 18H2.72724C2.53437 18 2.34939 17.921 2.213 17.7803C2.07662 17.6397 2 17.4489 2 17.25V3Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M7.09071 9H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.08984 3V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.9102 9H18.0009" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.9102 3V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const MeetingIcon = ({ size = 16, className = 'text-gray-500' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M13 8.00061L17.553 5.72461C17.7054 5.64844 17.8748 5.61249 18.045 5.62015C18.2152 5.62782 18.3806 5.67885 18.5256 5.76841C18.6706 5.85797 18.7902 5.98308 18.8733 6.13188C18.9563 6.28067 18.9999 6.44822 19 6.61861V13.3826C18.9999 13.553 18.9563 13.7205 18.8733 13.8693C18.7902 14.0181 18.6706 14.1432 18.5256 14.2328C18.3806 14.3224 18.2152 14.3734 18.045 14.3811C17.8748 14.3887 17.7054 14.3528 17.553 14.2766L13 12.0006V8.00061Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1 6C1 5.46957 1.21071 4.96086 1.58579 4.58579C1.96086 4.21071 2.46957 4 3 4H11C11.5304 4 12.0391 4.21071 12.4142 4.58579C12.7893 4.96086 13 5.46957 13 6V14C13 14.5304 12.7893 15.0391 12.4142 15.4142C12.0391 15.7893 11.5304 16 11 16H3C2.46957 16 1.96086 15.7893 1.58579 15.4142C1.21071 15.0391 1 14.5304 1 14V6Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const getIcon = (type: Suggestion['type']) => {
  switch (type) {
    case 'product':
      return <ProductIcon />
    case 'project':
      return <ProjectIcon />
    case 'meeting':
      return <MeetingIcon />
    default:
      return null
  }
}

const GlobalSearch: React.FC = () => {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 300)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const navigate = useNavigate()
  const customFetch = useCustomFetch()
  const containerRef = useRef<HTMLDivElement>(null)
  const { setSearchTerm } = useContext(DashboardContext)

  // Fetch suggestions when the debounced query changes
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setSuggestions([])
      return
    }

    const fetchData = async () => {
      try {
        const res = await customFetch(`/searchSuggestions?q=${encodeURIComponent(debouncedQuery)}`, 'GET')
        if (res && Array.isArray(res.suggestions)) {
          setSuggestions(res.suggestions)
          setIsOpen(true)
        }
      } catch (error) {
        console.error('Error fetching search suggestions', error)
      }
    }

    fetchData()
  }, [debouncedQuery])

  // Keep DashboardContext searchTerm in sync (used elsewhere for filtering projects)
  useEffect(() => {
    setSearchTerm(query)
  }, [query])

  // Reset highlight when list changes
  useEffect(() => {
    setHighlighted(0)
  }, [suggestions])

  // Ensure grouping order: products -> projects -> meetings then alphabetical.
  const orderedSuggestions = React.useMemo(() => {
    const order: Record<Suggestion['type'], number> = {
      product: 0,
      project: 1,
      meeting: 2,
    }
    return [...suggestions].sort((a, b) => {
      const diff = order[a.type] - order[b.type]
      if (diff !== 0) return diff
      return a.label.localeCompare(b.label)
    })
  }, [suggestions])

  const handleSelect = (index: number) => {
    const suggestion = orderedSuggestions[index]
    if (!suggestion) return
    setQuery('')
    setSuggestions([])
    setIsOpen(false)
    navigate(suggestion.path)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((prev) => (prev + 1) % orderedSuggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((prev) => (prev - 1 + orderedSuggestions.length) % orderedSuggestions.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleSelect(highlighted)
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative h-full">
      <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
        <SearchIcon size={14} className="text-gray-500 dark:text-gray-400" />
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        type="search"
        className="h-full w-full ps-10 pr-3 text-sm placeholder-gray-400 dark:placeholder-gray-500 text-gray-800 dark:text-gray-300 bg-grey-100 dark:bg-gray-700 rounded border border-grey-200 dark:border-gray-600 focus:outline-none focus:border-[#FF4A8E] focus:ring-2 focus:ring-[#FF4A8E]/30"
        placeholder="Search..."
        onFocus={() => suggestions.length && setIsOpen(true)}
        onBlur={() => {
          // delay so click event registers
          setTimeout(() => setIsOpen(false), 150)
        }}
      />
      {isOpen && orderedSuggestions.length > 0 && (
        <div className="fixed inset-0 bg-black/20 z-40" onMouseDown={() => setIsOpen(false)} />
      )}
      {isOpen && orderedSuggestions.length > 0 && (
        <ul className="absolute z-50 mt-2 w-[32rem] max-h-[28rem] overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-3 space-y-2">
          {['product', 'project', 'meeting'].map((group) => {
            const groupItems = orderedSuggestions.filter((s) => s.type === group)
            if (groupItems.length === 0) return null

            const headerLabel = group === 'product' ? 'PRODUCTS' : group === 'project' ? 'PROJECTS' : 'MEETINGS'

            return (
              <React.Fragment key={`grp-${group}`}>
                {/* Group header */}
                <li className="px-3 py-1 text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider select-none rounded bg-gray-50 dark:bg-gray-700/40">
                  {headerLabel}
                </li>
                {groupItems.map((s) => {
                  const itemIndex = orderedSuggestions.findIndex((os) => os === s)
                  return (
                    <li
                      key={`${s.type}-${s.id}`}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleSelect(itemIndex)
                      }}
                      className={cn(
                        'px-4 py-3 cursor-pointer flex items-center justify-between text-sm rounded-md',
                        itemIndex === highlighted
                          ? 'bg-[#FFEFF5] text-[#FF4A8E]'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      )}
                    >
                      <div className="flex items-start flex-1">
                        <span className="mr-3 mt-0.5">{getIcon(s.type)}</span>
                        <div className="flex flex-col">
                          <span>{s.label}</span>
                          {s.type === 'meeting' && s.date && (
                            <span className="text-xs text-gray-500">
                              {typeof s.date === 'number'
                                ? new Date(s.date).toLocaleString()
                                : new Date(s.date).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                    </li>
                  )
                })}
              </React.Fragment>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default GlobalSearch
